import styled from 'styled-components'
import { format } from 'date-fns'
import { Calendar, Clock } from 'lucide-react'
import { PiPaperclip, PiPencilSimpleLine, PiUserCircle } from 'react-icons/pi'
import { INote } from 'models/contact_companies'
import { Editor } from 'components/Editor/Editor'
import { JsonIntoUl } from 'components/json-into-ul'
import { API_HOST } from 'utils/http-client'

interface MobileNoteCardProps {
  note: INote
  onEdit?: (note: INote) => void
  onView?: (note: INote) => void
}

const CardContainer = styled.div`
  background: white;
  border: 1px solid #e2e2e2;
  border-radius: 8px;
  overflow: hidden;
  box-shadow:
    0 1px 3px 0 rgb(0 0 0 / 0.1),
    0 1px 2px -1px rgb(0 0 0 / 0.1);
`

const CardContent = styled.div`
  padding: 8px;
`

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`

const AuthorPill = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  background: #f3f4f6;
  border-radius: 9999px;
  border: 1px solid #e2e2e2;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 500;
  color: #374151;
`

const NoteContent = styled.div`
  font-size: 14px;
  color: #374151;
  margin-bottom: 8px;
  line-height: 1.5;
  max-height: 72px;
  overflow: hidden;
  position: relative;
  // mask-image: linear-gradient(to bottom, black 60%, transparent 100%);
  // -webkit-mask-image: linear-gradient(to bottom, black 60%, transparent 100%);

  /* Editor styling overrides for compact display */
  .ql-editor {
    padding: 0 !important;
    font-size: 14px !important;
  }
`

const AttachmentsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
  justify-content: flex-end;
`

const AttachmentLink = styled.a`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #2c6b4e;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`

const CardFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 11px;
  color: #6b7280;
  background: #f9fafb;
  padding: 8px 12px;
`

const DateInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

export function MobileNoteCard({ note, onEdit, onView }: MobileNoteCardProps) {
  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    onEdit?.(note)
  }

  const formatDateDisplay = (dateString: string | null | undefined) => {
    if (!dateString) return '-'
    try {
      return format(new Date(dateString), 'MM/dd/yyyy')
    } catch {
      return dateString
    }
  }

  const getAuthorName = () => {
    if (note.user?.first_name && note.user?.last_name) {
      return `${note.user.first_name} ${note.user.last_name}`
    }
    return note.user?.email || 'Unknown'
  }

  const getAttachmentUrl = (attachment: { file?: string }) => {
    if (!attachment.file) return '#'
    return attachment.file.startsWith('/')
      ? `${API_HOST}${attachment.file}`
      : attachment.file
  }

  const handleCardClick = () => {
    onView?.(note)
  }

  return (
    <CardContainer
      onClick={handleCardClick}
      style={{ cursor: onView ? 'pointer' : 'default' }}
    >
      <CardContent>
        <CardHeader>
          <AuthorPill>
            <PiUserCircle />
            {getAuthorName()}
          </AuthorPill>
          {onEdit && (
            <button
              onClick={handleEditClick}
              className="flex h-6 w-6 items-center justify-center rounded bg-surface-hover transition-colors hover:bg-gray-50"
              aria-label="Edit note"
            >
              <PiPencilSimpleLine className="h-3.5 w-3.5 text-brand" />
            </button>
          )}
        </CardHeader>

        <NoteContent>
          <JsonIntoUl
            value={note.content}
            fallback={
              <Editor
                value={note.content}
                readOnly={true}
                disableToolbar={true}
                borderless={true}
              />
            }
          />
        </NoteContent>

        {note.attachments && note.attachments.length > 0 && (
          <AttachmentsRow>
            {note.attachments.map((attachment, index) => (
              <AttachmentLink
                key={attachment.id || index}
                href={getAttachmentUrl(attachment)}
                target="_blank"
                rel="noopener noreferrer"
                // onClick={(e) => e.stopPropagation()}
              >
                <PiPaperclip size={12} />
                {attachment.original_filename || `Attachment ${index + 1}`}
              </AttachmentLink>
            ))}
          </AttachmentsRow>
        )}
      </CardContent>

      <CardFooter>
        <DateInfo>
          <Calendar size={12} />
          <span>Added {formatDateDisplay(note.created)}</span>
        </DateInfo>
        <DateInfo>
          <Clock size={12} />
          <span>Modified {formatDateDisplay(note.modified)}</span>
        </DateInfo>
      </CardFooter>
    </CardContainer>
  )
}
